import React, { useState, useEffect } from 'react';
import { supabase } from '../../../src/lib/supabase';
import { useAuth } from '../../../src/contexts/AuthContext';
import { logUsageEvent } from '../../../src/lib/logger';
import { createPortal } from 'react-dom';
import { RateioCredito, RateioCreditoItem } from '../../../types';
import { MESES_ABREV, normalizeMesReferencia } from '../../../src/lib/mesReferencia';
import { Zap, Plus, Trash2, Edit, Loader2, Link2 } from 'lucide-react';
import { useToast } from '../../../src/contexts/ToastContext';
import RateioManageModal from './RateioManageModal';
import UploadRateioModal from './UploadRateioModal';
import VincularPlanilhaModal from './VincularPlanilhaModal';

/** Chave numérica (ano*12+mês) pra ordenar "JUL/2026" cronologicamente — a
 *  coluna é texto livre, então ordenar por created_at fazia o mês exibido
 *  divergir da ordem da tabela sempre que dois rateios eram subidos fora de
 *  ordem (ex.: JUL/2026 subido depois de AGO/2026). */
const chaveOrdenacaoMes = (mesReferencia: string): number => {
  const normalizado = normalizeMesReferencia(mesReferencia);
  const [mes, ano] = normalizado.split('/');
  const mesIndex = MESES_ABREV.indexOf(mes);
  const anoNum = parseInt(ano, 10);
  if (mesIndex === -1 || isNaN(anoNum)) return -Infinity;
  return anoNum * 12 + mesIndex;
};

const RateioCreditosView: React.FC = () => {
  const { user } = useAuth();
  const [rateios, setRateios] = useState<RateioCredito[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedRateio, setSelectedRateio] = useState<RateioCredito | null>(null);
  const [rateioItems, setRateioItems] = useState<RateioCreditoItem[]>([]);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [rateioToDelete, setRateioToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Mês cuja planilha está aberta na prévia do vínculo automático (#5838).
  const [mesVinculo, setMesVinculo] = useState<string | null>(null);

  const { addToast } = useToast();

  const loadRateios = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('rateios_credito')
        .select('*');

      if (error) throw error;
      const ordenados = [...(data as RateioCredito[])].sort(
        (a, b) => chaveOrdenacaoMes(b.mes_referencia) - chaveOrdenacaoMes(a.mes_referencia)
      );
      setRateios(ordenados);
    } catch (error) {
      console.error('Error loading rateios', error);
      addToast('Erro ao carregar os rateios.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRateios();
  }, []);

  const handleManage = async (rateio: RateioCredito) => {
    setIsLoading(true);
    try {
      // Paginado: o PostgREST corta em 1.000 linhas sem erro, e o rateio de
      // AGO/2026 já tem 1.057 — as últimas 57 UCs sumiam do "Gerenciar".
      const itens: RateioCreditoItem[] = [];
      for (let de = 0; ; de += 1000) {
        const { data, error } = await supabase
          .from('rateios_credito_itens')
          .select('*')
          .eq('rateio_id', rateio.id)
          .order('id', { ascending: true })
          .range(de, de + 999);
        if (error) throw error;
        itens.push(...((data || []) as RateioCreditoItem[]));
        if (!data || data.length < 1000) break;
      }

      setSelectedRateio(rateio);
      setRateioItems(itens);
      setIsManageModalOpen(true);
    } catch (error) {
      console.error('Error loading items', error);
      addToast('Erro ao carregar itens do rateio', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!rateioToDelete) return;
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('rateios_credito')
        .delete()
        .eq('id', rateioToDelete);

      if (error) throw error;

      addToast('Rateio excluído com sucesso', 'success');
      logUsageEvent({ userId: user?.id, modulo: 'gestao', funcionalidade: 'rateio_antigo', acao: 'excluir_rateio', origem: 'acao' });
      loadRateios();
    } catch (error) {
      console.error('Error deleting rateio', error);
      addToast('Erro ao excluir rateio', 'error');
    } finally {
      setIsDeleting(false);
      setRateioToDelete(null);
    }
  };

  const formatDate = (isoString?: string) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString('pt-BR');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white font-poppins flex items-center gap-2">
            <Zap className="text-primary" /> Rateio de Créditos
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Gerencie os rateios mensais vindos das unidades geradoras
          </p>
        </div>

        <button
          onClick={() => setIsUploadModalOpen(true)}
          className="bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
        >
          <Plus size={18} /> Novo rateio
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        {isLoading && rateios.length === 0 ? (
          <div className="p-12 flex justify-center items-center">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : rateios.length === 0 ? (
          <div className="p-12 text-center flex flex-col items-center">
             <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-full mb-4 inline-block border border-gray-100 dark:border-gray-700">
               <Zap className="w-8 h-8 text-gray-300 dark:text-gray-600" />
             </div>
             <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Nenhum rateio encontrado</h3>
             <p className="text-gray-500 dark:text-gray-400">Clique em "Novo rateio" para importar uma planilha preenchida.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">
                  <th className="py-4 px-6 uppercase tracking-wider">Mês Ref. do Rateio</th>
                  <th className="py-4 px-6 uppercase tracking-wider">Usuário</th>
                  <th className="py-4 px-6 uppercase tracking-wider">Gerado em</th>
                  <th className="py-4 px-6 uppercase tracking-wider text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700 text-sm">
                {rateios.map((rateio) => (
                  <tr key={rateio.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-750/50 transition-colors">
                    <td className="py-4 px-6 font-medium text-gray-900 dark:text-gray-100">{rateio.mes_referencia}</td>
                    <td className="py-4 px-6 text-gray-600 dark:text-gray-300">{rateio.usuario_nome}</td>
                    <td className="py-4 px-6 text-gray-600 dark:text-gray-300">{formatDate(rateio.created_at)}</td>
                    <td className="py-4 px-6 text-right">
                       <div className="flex items-center justify-end gap-2">
                           <button
                             className="px-3 py-1.5 text-sm font-medium text-primary bg-primary/10 border border-primary/20 hover:bg-primary/20 dark:bg-primary/20 dark:hover:bg-primary/30 rounded-md transition-colors flex items-center gap-1.5"
                             onClick={() => handleManage(rateio)}
                             disabled={isLoading}
                           >
                             <Edit size={14} /> Gerenciar
                           </button>
                           <button
                             className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 dark:text-gray-200 dark:bg-gray-800 dark:border-gray-600 dark:hover:bg-gray-700 rounded-md transition-colors flex items-center gap-1.5"
                             onClick={() => setMesVinculo(rateio.mes_referencia)}
                             title="Ligar as UCs às usinas pelo número da usina desta planilha (abre uma prévia)"
                           >
                             <Link2 size={14} /> Vincular UCs
                           </button>
                           <button
                             className="p-1.5 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors"
                             onClick={() => setRateioToDelete(rateio.id)}
                             title="Excluir rateio"
                           >
                             <Trash2 size={16} />
                           </button>
                       </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {mesVinculo && (
        <VincularPlanilhaModal mesReferencia={mesVinculo} onClose={() => setMesVinculo(null)} />
      )}

      {isUploadModalOpen && (
        <UploadRateioModal
           onClose={() => setIsUploadModalOpen(false)}
           onSuccess={() => { setIsUploadModalOpen(false); loadRateios(); }}
        />
      )}

      {isManageModalOpen && selectedRateio && (
        <RateioManageModal
           rateio={selectedRateio}
           itens={rateioItems}
           onClose={() => setIsManageModalOpen(false)}
        />
      )}

      {rateioToDelete && createPortal(
         <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in custom-scrollbar">
           <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center animate-slide-up">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                 <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 font-poppins">Excluir Rateio</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                 Tem certeza que deseja excluir esse rateio? Todos os itens associados serão removidos permanentemente. Esta ação não pode ser desfeita.
              </p>
              <div className="flex gap-3">
                 <button
                   onClick={() => setRateioToDelete(null)}
                   className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600 transition-colors"
                 >
                   Cancelar
                 </button>
                 <button
                   onClick={handleDelete}
                   disabled={isDeleting}
                   className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors flex items-center justify-center disabled:opacity-50"
                 >
                   {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sim, excluir'}
                 </button>
              </div>
           </div>
         </div>,
         document.body
      )}
    </div>
  );
};

export default RateioCreditosView;
